// 대기 클래스별 색상군 — Oracle Enterprise Manager 의 Top Activity / ASH Analytics 관례를 따른다.
//
// ⚠ 출처에 대한 정직한 표기: Oracle 은 이 배색을 **공식 표준 문서로 배포하지 않는다.**
//    아래 색상(hue)은 OEM 화면에서 오랫동안 쓰여 성능 분석 문헌에 반복 인용된 관례를 따른 것이고,
//    정확한 hex 값은 OEM 과 다르다. 즉 "표준 준수"가 아니라 **관례 정렬**이다.
//    널리 통용되어 사람이 색만 보고 성격을 짐작할 수 있는 것:
//      초록 = CPU · 파랑 = User I/O · 주황 = Commit · 빨강 = Application · 갈색 = Concurrency
//
// 색은 **클래스가 정하고**, 같은 클래스 안의 여러 이벤트는 명도만 달리한다.
// 이벤트마다 무작위 색을 주면 "파란 것들은 I/O" 같은 읽기가 불가능해진다.
const WAIT_CLASS_FAMILY = {
    'CPU':            ['#15803d', '#16a34a', '#22c55e', '#4ade80', '#86efac', '#bbf7d0'],  // 초록
    'User I/O':       ['#1e3a8a', '#1d4ed8', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe'],  // 파랑
    'System I/O':     ['#155e75', '#0e7490', '#0891b2', '#22d3ee', '#67e8f9', '#a5f3fc'],  // 청록
    'Commit':         ['#9a3412', '#c2410c', '#ea580c', '#fb923c', '#fdba74', '#fed7aa'],  // 주황
    'Application':    ['#991b1b', '#b91c1c', '#ef4444', '#f87171', '#fca5a5', '#fecaca'],  // 빨강
    'Concurrency':    ['#78350f', '#92400e', '#b45309', '#d97706', '#f59e0b', '#fbbf24'],  // 갈색
    'Configuration':  ['#854d0e', '#a16207', '#ca8a04', '#eab308', '#facc15', '#fde047'],  // 황토
    'Cluster':        ['#86198f', '#a21caf', '#c026d3', '#d946ef', '#e879f9', '#f0abfc'],  // 자주
    'Network':        ['#115e59', '#0f766e', '#14b8a6', '#2dd4bf', '#5eead4', '#99f6e4'],  // 틸
    'Administrative': ['#3730a3', '#4338ca', '#6366f1', '#818cf8', '#a5b4fc', '#c7d2fe'],  // 남보라
    'Scheduler':      ['#5b21b6', '#6d28d9', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe'],  // 보라
    'Queueing':       ['#831843', '#9d174d', '#db2777', '#ec4899', '#f472b6', '#f9a8d4'],  // 분홍
    'Idle':           ['#6b7280', '#9ca3af', '#d1d5db', '#e5e7eb', '#f3f4f6', '#f9fafb'],  // 회색
    'Other':          ['#701a3f', '#9f1239', '#be123c', '#e11d48', '#fb7185', '#fda4af'],  // 진분홍
};

const FALLBACK_FAMILY = ['#334155', '#475569', '#64748b', '#94a3b8', '#cbd5e1', '#e2e8f0'];

/**
 * **클래스 하나의 대표색.** 이벤트별 명도까지 나눌 자리가 없는 그림(세션 타임라인 등)에서
 * 쓴다 — 색만 보고 성격을 짐작할 수 있어야 한다는 원칙은 같으므로 같은 색상군에서 꺼낸다.
 * 어두운 테마에서는 한 단 밝은 쪽을 쓴다(어두운 바탕에서 진한 색은 뭉개진다).
 */
export function waitClassColor(waitClass, isDarkMode = false) {
    const family = WAIT_CLASS_FAMILY[waitClass] || FALLBACK_FAMILY;
    return family[isDarkMode ? 3 : 2];
}

/** '기타'(상위 N 밖 합산) 는 개별 항목이 아니므로 클래스 색을 쓰지 않는다 */
export const OTHERS_COLOR = { light: '#9ca3af', dark: '#6b7280' };

/**
 * 이벤트 목록 → { event: color } 매핑.
 * 같은 클래스 안에서는 등장 순서대로 명도 단계를 배정한다.
 *
 * @param {string[]} events     계열 이름(이벤트) 목록
 * @param {Object}   classByEvent  {event: waitClass} — 서버가 준 맵
 */
export function buildWaitClassColors(events, classByEvent = {}) {
    const out = {};
    (events || []).forEach(ev => {
        const cls = waitClassOf(ev, classByEvent);
        const family = WAIT_CLASS_FAMILY[cls] || FALLBACK_FAMILY;
        out[ev] = family[hashIndex(ev, family.length)];
    });
    return out;
}

/**
 * 이벤트 이름 → 명도 단계 인덱스 (결정적).
 *
 * <b>등장 순서로 배정하지 않는 이유</b>: 순서로 정하면 같은 이벤트가 탭마다 다른 색이 된다.
 * 실시간 성능과 성능 이력은 상위 N 집합이 서로 달라 순서가 어긋나기 때문이다.
 * "db file sequential read 는 어디서나 이 파랑" 이 성립해야 두 탭을 오가며 눈으로 대조할 수 있다.
 *
 * 대가: 같은 클래스의 두 이벤트가 같은 명도로 겹칠 수 있다. 그때도 **클래스(색상군)는 맞고**
 * 범례에 이름이 있으므로 오독이 아니라 구분이 덜 되는 정도다 — 탭 간 색이 바뀌는 쪽이 더 나쁘다.
 */
function hashIndex(name, mod) {
    let h = 0;
    for (let i = 0; i < name.length; i++) {
        h = (h * 31 + name.charCodeAt(i)) | 0;
    }
    return Math.abs(h) % mod;
}

/** 범례 옆에 클래스명을 보여주기 위한 조회 (툴팁용) */
export function waitClassOf(event, classByEvent = {}) {
    return classByEvent[event] || (event === 'ON CPU' ? 'CPU' : null);
}
